import type { BillingProfile } from 'gpcore-grpc/gpcore/api/cloud/v1/billing_pb';
import { useField, useForm } from 'vee-validate';
import { computed, onUnmounted, reactive, ref, watch, watchEffect } from 'vue';
import { object as yupObject, string as yupString } from 'yup';
import { useSessionStore } from 'gpcore-apikit/store/pinia/session';
import { UserTag } from 'gpcore-grpc/gpcore/api/cloud/v1/user_pb';
import type { SelectObjectItem } from '@gportal/uikit';

export interface BillingProfileModel {
  company: string;
  name: string;
  email?: string;

  vatId: string;

  street: string;
  postcode: string;
  city: string;
  state?: string;

  country: { key: string; label: string };
}

export default (props: { billingProfile: BillingProfile }) => {
  const requiredFields = {
    company: yupString().required(),
    vatId: yupString().required(),
    name: yupString().required(),
    street: yupString().required(),
    postcode: yupString().required(),
    city: yupString().required(),
  };

  // check if company is not required
  const sessionStore = useSessionStore();
  if (sessionStore.session.user.tags.includes(UserTag.NO_COMPANY)) {
    delete requiredFields.company;
    delete requiredFields.vatId;
  }

  const validationSchema = ref(yupObject(requiredFields));

  const { errors, handleSubmit } = useForm<BillingProfileModel>({
    validationSchema,
  });

  // company
  const {
    value: company,
    setTouched: setCompanyTouched,
    resetField: resetCompanyField,
    meta: companyMeta,
  } = useField<string>('company', undefined, {
    initialValue:
      props.billingProfile != null && props.billingProfile.company != null
        ? props.billingProfile.company.name
        : '',
  });
  // name
  const {
    value: name,
    setTouched: setNameTouched,
    resetField: resetNameField,
    meta: nameMeta,
  } = useField<string>('name', undefined, {
    initialValue:
      props.billingProfile != null && props.billingProfile.name != null
        ? props.billingProfile.name
        : '',
  });
  // vatid
  const {
    value: vatId,
    setTouched: setVatIdTouched,
    resetField: resetVatIdField,
    meta: vatIdMeta,
  } = useField<string>('vatId', undefined, {
    initialValue:
      props.billingProfile != null && props.billingProfile.company != null
        ? props.billingProfile.company.vatId
        : '',
  });
  // street
  const {
    value: street,
    setTouched: setStreetTouched,
    resetField: resetStreetField,
    meta: streetMeta,
  } = useField<string>('street', undefined, {
    initialValue:
      props.billingProfile != null && props.billingProfile.street != null
        ? props.billingProfile.street
        : '',
  });
  // postcode
  const {
    value: postcode,
    setTouched: setPostcodeTouched,
    resetField: resetPostcodeField,
    meta: postcodeMeta,
  } = useField<string>('postcode', undefined, {
    initialValue:
      props.billingProfile != null && props.billingProfile.postcode != null
        ? props.billingProfile.postcode
        : '',
  });
  // state
  const {
    value: state,
    setTouched: setStateTouched,
    resetField: resetStateField,
    meta: stateMeta,
  } = useField<string>('state', undefined, {
    initialValue:
      props.billingProfile != null && props.billingProfile.state != null
        ? props.billingProfile.state
        : '',
  });
  // city
  const {
    value: city,
    setTouched: setCityTouched,
    resetField: resetCityField,
    meta: cityMeta,
  } = useField<string>('city', undefined, {
    initialValue:
      props.billingProfile != null && props.billingProfile.city != null
        ? props.billingProfile.city
        : '',
  });
  // email
  const email = ref<string>(
    props.billingProfile != null && props.billingProfile.billingEmail != null
      ? props.billingProfile.billingEmail
      : '',
  );
  // country
  const country = ref<{ key: string; label: string }>(
    props.billingProfile != null && props.billingProfile.countryCode != null
      ? { key: props.billingProfile.countryCode, label: null }
      : { key: 'DE', label: null },
  );

  const handleInputWatchEffect = watchEffect(() => {
    setCompanyTouched(company.value.length > 0 || companyMeta.touched);
    setNameTouched(name.value.length > 0 || nameMeta.touched);

    // since vatid is a optional field, we need to check if it is not null
    if (vatId.value != null) {
      setVatIdTouched(vatId.value.length > 0 || vatIdMeta.touched);
    } else {
      // otherwise we need to set it to touched if it was touched
      setVatIdTouched(true);
    }

    setStreetTouched(street.value.length > 0 || streetMeta.touched);
    setPostcodeTouched(postcode.value.length > 0 || postcodeMeta.touched);
    setStateTouched(state.value.length > 0 || stateMeta.touched);
    setCityTouched(city.value.length > 0 || cityMeta.touched);
  });

  const form = reactive({
    company,
    name,
    vatId,
    email,
    street,
    postcode,
    city,
    state,
    country,
  });

  const supportsVatID = computed(() => {
    // if user needs a company, vat id is required
    if (!sessionStore.session.user.tags.includes(UserTag.NO_COMPANY)) {
      return true;
    }

    // If a company is entered, vat id is required
    return company.value !== '';
  });

  const updateFormField = (field: keyof BillingProfileModel, value: string | SelectObjectItem) => {
    if (field === 'country') {
      form[field] = value as { key: string; label: string };
    } else {
      form[field] = value as string;
    }
  };

  const reset = () => {
    resetCompanyField();
    resetNameField();
    resetVatIdField();
    form.email = '';
    resetStreetField();
    resetPostcodeField();
    resetCityField();
    resetStateField();
    form.country = { key: 'DE', label: null };
  };

  watch(
    () => props.billingProfile,
    (newValue) => {
      if (newValue == null || newValue.company == null) return;

      form.company = newValue.company.name;
      form.name = newValue.name;
      form.vatId = newValue.company.vatId;
      form.email = newValue.billingEmail;
      form.street = newValue.street;
      form.postcode = newValue.postcode;
      form.city = newValue.city;
      form.state = newValue.state;
      form.country = { key: newValue.countryCode, label: null };
    },
  );

  onUnmounted(() => {
    handleInputWatchEffect();
  });

  return {
    errors,
    handleSubmit,
    supportsVatID,
    form,
    reset,
    updateFormField,
  };
};
